import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Ship } from '../../services/shipService';
import { Zap } from 'lucide-react';
import styles from './ShipCard.module.css';

interface ShipCardProps {
  ship: Ship;
}

const ShipCard: React.FC<ShipCardProps> = ({ ship }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/ship-database/${ship.slug}`);
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US').format(price) + ' aUEC';
  };

  const getStatusBadge = (status: string | null) => {
    if (!status) return null;
    const statusClass = status.toLowerCase().replace(/-/g, '');
    return <span className={`${styles.statusBadge} ${styles[statusClass]}`}>{status}</span>;
  };

  const getSizeBadge = (size: string | null) => {
    if (!size) return null;
    return <span className={styles.sizeBadge}>{size}</span>;
  };

  return (
    <div className={styles.shipCard} onClick={handleClick}>
      <div className={styles.imageContainer}>
        {ship.thumbnailUrl ? (
          <img src={ship.thumbnailUrl} alt={ship.name} className={styles.shipImage} />
        ) : (
          <img src="/images/ship-art-placeholder.png" alt="Artwork coming soon" className={styles.shipImage} />
        )}
        <div className={styles.badges}>
          {getStatusBadge(ship.productionStatus)}
          {getSizeBadge(ship.size)}
        </div>
      </div>

      <div className={styles.content}>
        <h3 className={styles.shipName}>{ship.name}</h3>
        <p className={styles.manufacturer}>{ship.manufacturer || 'Unknown'}</p>
        
        <div className={styles.role}>
          <span className={styles.roleIcon}>⚙️</span>
          {ship.role || 'Multi-Role'}
        </div>

        <div className={styles.specs}>
          <div className={styles.spec}>
            <span className={styles.specLabel}>Crew</span>
            <span className={styles.specValue}>
              {ship.crewMin || 1}-{ship.crewMax || 1}
            </span>
          </div>
          <div className={styles.spec}>
            <span className={styles.specLabel}>Cargo</span>
            <span className={styles.specValue}>{ship.cargoCapacity || 0} SCU</span>
          </div>
        </div>

        {/* Show hardpoint indicator if data is available */}
        {ship.hardpoints && ship.hardpoints.length > 0 && (
          <div className={styles.hardpointIndicator}>
            <Zap size={14} />
            <span>Enhanced Data Available</span>
          </div>
        )}

        <div className={styles.price}>
          {formatPrice(ship.priceAUEC)}
        </div>
      </div>
    </div>
  );
};

export default ShipCard;
